import {
  normalizeHomeEconomy,
  type HomeEconomy,
} from "@/home/economy";
import type { HomeRoomData } from "@/home/storage";

const DB_NAME = "mind-survival-home";
const STORE = "rooms";
const DB_VERSION = 1;

function economyKey(userId: string): string {
  return `economy:${userId}`;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("เปิด IndexedDB ไม่สำเร็จ"));
  });
  return dbPromise;
}

function idbGet<T>(key: string): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(key);
        req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
        req.onerror = () => reject(req.error ?? new Error("อ่าน IndexedDB ไม่สำเร็จ"));
      }),
  );
}

function idbSet(key: string, value: unknown): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error("เขียน IndexedDB ไม่สำเร็จ"));
        tx.objectStore(STORE).put(value, key);
      }),
  );
}

export function isStorageQuotaError(err: unknown): boolean {
  if (err instanceof DOMException) {
    return err.name === "QuotaExceededError" || err.code === 22;
  }
  return false;
}

export async function loadIdbValue<T>(key: string): Promise<T | null> {
  try {
    return await idbGet<T>(key);
  } catch {
    return null;
  }
}

export async function setIdbValue(key: string, value: unknown): Promise<void> {
  await idbSet(key, value);
}

export async function loadHomeRoomFromIdb(userId: string): Promise<HomeRoomData | null> {
  return loadIdbValue<HomeRoomData>(userId);
}

export async function saveHomeRoomToIdb(userId: string, data: HomeRoomData): Promise<void> {
  await setIdbValue(userId, data);
}

export async function deleteHomeRoomIdbKey(key: string): Promise<void> {
  await openDb().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, "readwrite");
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error("ลบ IndexedDB ไม่สำเร็จ"));
        tx.objectStore(STORE).delete(key);
      }),
  );
}

export async function loadHomeEconomyFromIdb(userId: string): Promise<HomeEconomy | null> {
  try {
    const raw = await idbGet<unknown>(economyKey(userId));
    if (!raw) return null;
    return normalizeHomeEconomy(raw);
  } catch {
    return null;
  }
}

export async function saveHomeEconomyToIdb(
  userId: string,
  economy: HomeEconomy,
): Promise<void> {
  const { bumpPlayerSaveSectionTimestamp } = await import(
    "@/cloud/player-cloud-timestamps"
  );
  bumpPlayerSaveSectionTimestamp(userId, "home");
  await idbSet(economyKey(userId), economy);
  const { schedulePlayerCloudPush } = await import("@/cloud/player-cloud-scheduler");
  schedulePlayerCloudPush(userId, "home");
}
